import { getPendingCode, clearPendingCode } from './pendingCode';
import { bindCode } from './codesClient';
import { auth } from './firebase';
import { ensureLicenseToken } from './licenseToken';

/**
 * Bind a registration code that was stashed at signup (pendingCode) once the
 * user's email is verified. This is the SINGLE canonical bind path — every
 * verification flow (the /login AuthForm verify screen, the /activate
 * VerifyEmailScreen, and the useAuth onAuthChange/focus backstop) routes
 * through here so the behavior can't drift.
 *
 * Two traps this guards against, both of which silently broke earlier fixes:
 *   1. GET /account does NOT return a verified flag, so we gate on the Firebase
 *      user object (`user.emailVerified`) after a reload(), never on /account.
 *   2. The ID token's `email_verified` claim lags out-of-band verification (the
 *      emailed link) until a FORCED refresh. The server-side link-code gate
 *      checks that claim, so we always mint the token with getIdToken(true).
 *
 * Idempotent: a second call after a successful bind sees no pending code and
 * no-ops. Clears the pending code on success OR a permanent rejection
 * (404 invalid / 409 already-linked / 410 revoked); keeps it for a transient
 * failure (verification still propagating, offline) so a later trigger retries.
 *
 * @returns {Promise<{bound: boolean, hadPending: boolean, status: number}>}
 */
export async function bindPendingCode() {
  const pending = getPendingCode();
  if (!pending) return { bound: false, hadPending: false, status: 0 };

  const user = auth.currentUser;
  if (!user) return { bound: false, hadPending: true, status: 0 };

  try {
    // reload() refreshes emailVerified; getIdToken(true) refreshes the token's
    // email_verified CLAIM. Both are required — they update different things.
    await user.reload();
    if (!user.emailVerified) return { bound: false, hadPending: true, status: 0 };

    const token = await user.getIdToken(true);
    const r = await bindCode(token, pending);
    if (r.ok || [404, 409, 410].includes(r.status)) clearPendingCode();
    if (r.ok) {
      // Code just activated on this device — issue the offline license token
      // (this is the canonical bind path, so it covers every verification flow).
      await ensureLicenseToken().catch(() => {});
    }
    return { bound: Boolean(r.ok), hadPending: true, status: r.status || 0 };
  } catch {
    // Network/transient — keep pending for the next trigger to retry.
    return { bound: false, hadPending: true, status: 0 };
  }
}
